/**
 * Design tokens — Adverdict "Decision Cut" brand system.
 * Source of truth: adverdict_brand_assets_corrected/05_design_tokens/
 *
 * Color values resolve through the CSS variables defined in src/index.css,
 * so inline styles built from these tokens follow the active light/dark theme.
 */

export const colors = {
  bg: {
    canvas: 'rgb(var(--color-canvas))',
    surface: 'rgb(var(--color-surface))',
    surfaceSubtle: 'rgb(var(--color-surface-subtle))',
    sidebar: 'rgb(var(--color-sidebar-bg))',
    overlay: 'rgb(var(--color-overlay) / 0.55)',
  },
  text: {
    primary: 'rgb(var(--color-text-primary))',
    secondary: 'rgb(var(--color-text-secondary))',
    muted: 'rgb(var(--color-text-muted))',
    inverse: 'rgb(var(--color-text-inverse))',
    disabled: 'rgb(var(--color-text-disabled))',
  },
  border: {
    subtle: 'rgb(var(--color-border-subtle))',
    default: 'rgb(var(--color-border-default))',
    strong: 'rgb(var(--color-border-strong))',
  },
  brand: {
    primary: 'rgb(var(--color-brand))',
    primaryHover: 'rgb(var(--color-brand-hover))',
    primaryActive: 'rgb(var(--color-brand-active))',
    primarySoft: 'rgb(var(--color-brand) / 0.10)',
  },
  status: {
    success: 'rgb(var(--color-success))',
    successSoft: 'rgb(var(--color-success-strong) / 0.12)',
    warning: 'rgb(var(--color-warning))',
    warningSoft: 'rgb(var(--color-warning-strong) / 0.14)',
    danger: 'rgb(var(--color-danger))',
    dangerSoft: 'rgb(var(--color-danger-strong) / 0.12)',
    info: 'rgb(var(--color-info))',
    infoSoft: 'rgb(var(--color-info) / 0.12)',
  },
  data: {
    green: 'rgb(var(--color-data-green))',
    blue: 'rgb(var(--color-data-blue))',
    orange: 'rgb(var(--color-data-orange))',
    red: 'rgb(var(--color-data-red))',
    cyan: 'rgb(var(--color-data-cyan))',
    purple: 'rgb(var(--color-data-purple))',
  },
} as const;

export const typography = {
  fontFamily: {
    sans: [
      'Satoshi',
      'Inter',
      'ui-sans-serif',
      'system-ui',
      '-apple-system',
      'Segoe UI',
      'Roboto',
      'Arial',
      'sans-serif',
    ],
    editorial: ['Instrument Serif', 'Georgia', 'serif'],
  },
  fontWeight: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    black: 800,
  },
  fontSize: {
    xs: '12px',
    sm: '13px',
    md: '14px',
    lg: '16px',
    xl: '18px',
    '2xl': '22px',
    '3xl': '28px',
    '4xl': '36px',
  },
  lineHeight: {
    tight: 1.15,
    snug: 1.25,
    normal: 1.45,
    relaxed: 1.6,
  },
  letterSpacing: {
    tight: '-0.02em',
    normal: '0em',
    wide: '0.06em',
    wider: '0.10em',
  },
} as const;

export const spacing = {
  0: '0px',
  1: '4px',
  2: '8px',
  3: '12px',
  4: '16px',
  5: '20px',
  6: '24px',
  7: '28px',
  8: '32px',
  10: '40px',
  12: '48px',
} as const;

export const radius = {
  sm: '8px',
  md: '12px',
  lg: '16px',
  xl: '20px',
  pill: '999px',
} as const;

export const shadow = {
  sm: 'var(--shadow-sm)',
  md: 'var(--shadow-md)',
  lg: 'var(--shadow-lg)',
  insetBorder: 'inset 0 0 0 1px rgb(var(--color-border-default))',
} as const;

export const motion = {
  duration: {
    fast: '120ms',
    base: '180ms',
    slow: '260ms',
  },
  easing: {
    standard: 'cubic-bezier(0.2, 0, 0, 1)',
    emphasized: 'cubic-bezier(0.2, 0, 0, 1.2)',
  },
} as const;

export const layout = {
  sidebarWidth: '260px',
  topbarHeight: '72px',
  containerMaxWidth: '1240px',
  contentPadding: spacing[8],
} as const;

// Decision colors for Scale/Fix/Kill
export const decisionColors = {
  Scale: {
    bg: colors.status.successSoft,
    text: colors.status.success,
  },
  Fix: {
    bg: colors.status.warningSoft,
    text: colors.status.warning,
  },
  Kill: {
    bg: colors.status.dangerSoft,
    text: colors.status.danger,
  },
} as const;

// Confidence level colors
export const confidenceColors = {
  High: colors.status.success,
  Medium: colors.status.warning,
  Low: colors.status.danger,
} as const;

export type Decision = 'Scale' | 'Fix' | 'Kill';
export type Confidence = 'High' | 'Medium' | 'Low';
